import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { isConfigFlagEnabled } from '../../config';
import { isSupportedPlayerServerSettingType } from '../../types/playerServerSettingsTypes';
import { DEFAULT_USER_CLIENT_SETTINGS } from '../../utils/userClientSettingsUtils';
import { isTestEnvironment } from '../../utils/utils';
import { CLEAR_REBUY_DELAY, CLEAR_SELF_DATA_STATE, REBUY_SUCCESS_FROM_INBOUND, SET_FETCHED_SELF_INFO, SET_IS_SELF_INFO_LOADING, SET_PLAYER_SERVER_SETTINGS_FROM_INBOUND, SET_SELF_CHIPS, SET_SELF_DATA_FROM_INBOUND, SET_SELF_POWER_PLAYER_STATUS, SET_USER_CLIENT_SETTINGS, UPDATE_FREE_DAILY_BONUS_DELAY, UPDATE_JACKS_AND_AMMO_FROM_INBOUND, UPDATE_PLAYER_SERVER_SETTING_FROM_INBOUND } from '../actions/selfDataActions';
export const initialState = {
    userClientSettings: cloneDeep(DEFAULT_USER_CLIENT_SETTINGS),
    playerServerSettingLabels: {},
    isLoadingSelfInfo: false,
    isFourColorDeckEnabled: false,
    isSideBettingSettingEnabled: false,
    isShowChipsAsBbMultipleEnabled: false
};
export function selectIsSideBetsPanelEnabled(state) {
    if (!isConfigFlagEnabled('sideBets')) {
        return false;
    }
    if (!state.selfData.isSideBettingSettingEnabled) {
        return false;
    }
    return true;
}
export function selectMyNumJacks(state) {
    return state.selfData.numJacks;
}
export function selectMyNumChips(state) {
    return state.selfData.numChips;
}
export function selectPanelPositionSetting(state) {
    return state.selfData.userClientSettings.PANEL_POSITION.position;
}
export function selectIsAnimatedRoomBackgroundsOn(state) {
    return state.selfData.playerServerSettingLabels.animated_room_backgrounds === 'On';
}
function draftUpdateIsFourColorDeckEnabled(draft) {
    draft.isFourColorDeckEnabled =
        draft.isPowerPlayer === true && draft.playerServerSettingLabels.four_color_deck === 'On';
}
function draftUpdateIsSideBettingSettingEnabled(draft) {
    draft.isSideBettingSettingEnabled = draft.playerServerSettingLabels.sidebets_enabled === 'On';
}
function draftUpdateIsShowChipsAsBbMultipleEnabled(draft) {
    if (isConfigFlagEnabled('showChipsAsBbMultipleAvailable')) {
        draft.isShowChipsAsBbMultipleEnabled = draft.playerServerSettingLabels.show_chips_as_bb_multiple === 'On';
    }
}
function draftUpdateFromPlayerServerSettings(draft) {
    draftUpdateIsFourColorDeckEnabled(draft);
    draftUpdateIsSideBettingSettingEnabled(draft);
    draftUpdateIsShowChipsAsBbMultipleEnabled(draft);
}
function draftUpdateFromPowerPlayerStatus(draft) {
    draftUpdateIsFourColorDeckEnabled(draft);
}
function draftUpdateRebuyAvailableTimestampMs(draft, rebuyDelaySecondsLeft) {
    if (!rebuyDelaySecondsLeft) {
        delete draft.rebuyAvailableTimestampMs;
    }
    else {
        // add a few seconds to account for any communication delays, and convert to a relative ms timestamp
        const extraSeconds = isTestEnvironment() ? 0 : 10;
        draft.rebuyAvailableTimestampMs = performance.now() + (rebuyDelaySecondsLeft + extraSeconds) * 1000;
    }
}
function draftUpdateFreeDailyBonusAvailableTimestampMs(draft, freeDailyBonusSecondsLeft) {
    if (freeDailyBonusSecondsLeft === -1) {
        // -1 means feature is turned off on the server or there was an error
        draft.freeDailyBonusAvailableTimestampMs = -1;
    }
    else if (freeDailyBonusSecondsLeft === 0) {
        // setting the timestamp to undefined means free bonus is available now
        delete draft.freeDailyBonusAvailableTimestampMs;
    }
    else {
        draft.freeDailyBonusAvailableTimestampMs = performance.now() + freeDailyBonusSecondsLeft * 1000;
    }
}
const selfDataReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case CLEAR_SELF_DATA_STATE: {
                return initialState;
            }
            case SET_SELF_DATA_FROM_INBOUND: {
                const { id, username, numJacks, numChips, numAmmo, isAdmin, isPowerPlayer, rebuyDelaySecondsLeft, freeDailyBonusSecondsLeft } = action.payload;
                draft.id = id;
                draft.username = username;
                draft.numJacks = numJacks;
                draft.numChips = numChips;
                draft.numAmmo = numAmmo;
                draft.isAdmin = isAdmin;
                draft.isPowerPlayer = isPowerPlayer;
                draftUpdateRebuyAvailableTimestampMs(draft, rebuyDelaySecondsLeft);
                draftUpdateFreeDailyBonusAvailableTimestampMs(draft, freeDailyBonusSecondsLeft);
                draftUpdateFromPowerPlayerStatus(draft);
                break;
            }
            case UPDATE_JACKS_AND_AMMO_FROM_INBOUND: {
                const { numJacks, numAmmo } = action.payload;
                draft.numJacks = numJacks;
                draft.numAmmo = numAmmo;
                break;
            }
            case SET_SELF_CHIPS: {
                draft.numChips = action.payload;
                break;
            }
            case SET_SELF_POWER_PLAYER_STATUS: {
                draft.isPowerPlayer = action.payload;
                draftUpdateFromPowerPlayerStatus(draft);
                break;
            }
            case SET_USER_CLIENT_SETTINGS: {
                draft.userClientSettings = action.payload;
                break;
            }
            case SET_PLAYER_SERVER_SETTINGS_FROM_INBOUND: {
                const inboundPlayerServerSettings = action.payload;
                inboundPlayerServerSettings.forEach((inboundPlayerServerSetting) => {
                    if (isSupportedPlayerServerSettingType(inboundPlayerServerSetting.type)) {
                        draft.playerServerSettingLabels[inboundPlayerServerSetting.type] =
                            inboundPlayerServerSetting.values[inboundPlayerServerSetting.currentValueIndex];
                    }
                });
                draftUpdateFromPlayerServerSettings(draft);
                break;
            }
            case UPDATE_PLAYER_SERVER_SETTING_FROM_INBOUND: {
                const { inboundPlayerServerSetting } = action.payload;
                if (isSupportedPlayerServerSettingType(inboundPlayerServerSetting.type)) {
                    draft.playerServerSettingLabels[inboundPlayerServerSetting.type] =
                        inboundPlayerServerSetting.values[inboundPlayerServerSetting.currentValueIndex];
                }
                draftUpdateFromPlayerServerSettings(draft);
                break;
            }
            case SET_IS_SELF_INFO_LOADING: {
                draft.isLoadingSelfInfo = action.payload;
                if (draft.isLoadingSelfInfo) {
                    delete draft.fetchedSelfInfo;
                }
                break;
            }
            case SET_FETCHED_SELF_INFO: {
                draft.fetchedSelfInfo = action.payload;
                draft.isLoadingSelfInfo = false;
                break;
            }
            case REBUY_SUCCESS_FROM_INBOUND: {
                draftUpdateRebuyAvailableTimestampMs(draft, action.payload.rebuyDelaySecondsLeft);
                break;
            }
            case CLEAR_REBUY_DELAY: {
                draftUpdateRebuyAvailableTimestampMs(draft, 0);
                break;
            }
            case UPDATE_FREE_DAILY_BONUS_DELAY: {
                const freeDailyBonusSecondsLeft = action.payload;
                draftUpdateFreeDailyBonusAvailableTimestampMs(draft, freeDailyBonusSecondsLeft);
                break;
            }
            default: {
                return state;
            }
        }
    });
};
export default selfDataReducer;
